import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllNotes, getSharedNotes, deleteNote, shareNote, emailNoteToRecipients } from "./NoteService";
import { extractPlainText } from "./NoteLinkUtils";
import "./NoteList.css";

export default function NoteList() {
  const [notes, setNotes] = useState([]);
  const [sharedNotes, setSharedNotes] = useState([]);
  const [viewMode, setViewMode] = useState("mine");
  const [expandedNotes, setExpandedNotes] = useState({});
  const [selectedTag, setSelectedTag] = useState("ALL");
  const nav = useNavigate();
  useEffect(() => { load(); }, []);

  const load = async () => {
    const data = await getAllNotes();
    setNotes(data);
  };

  const loadShared = async () => {
    const data = await getSharedNotes();
    setSharedNotes(data);
  };

  useEffect(() => {
    if (viewMode === "shared") {
      loadShared();
    }
  }, [viewMode]);

  const remove = async (note) => {
    if (note.deleteProtected) {
      alert("This note is protected from deletion. Disable protection to delete it.");
      return;
    }
    if (!window.confirm("Delete this note?")) return;
    const res = await deleteNote(note.id);
    if (res && !res.ok) {
      let detail = "";
      try {
        const text = await res.text();
        if (text) detail = `\n${text}`;
      } catch (_e) {}
      alert(`Delete failed (${res.status || "network"}).${detail}`);
      return;
    }
    load();
  };

  const handleShare = async (note) => {
    const input = window.prompt("Share with (comma-separated user IDs or emails):");
    if (!input) return;
    const tokens = input.split(",").map((s) => s.trim()).filter(Boolean);
    const emails = tokens.filter((t) => t.includes("@"));
    const userIds = tokens.filter((t) => !t.includes("@"));
    const sendEmail = window.confirm("Send email notifications to recipients?");
    const includeAttachments = window.confirm("Include attachments in share?");
    const res = await shareNote(note.id, { userIds, emails, sendEmail, includeAttachments });
    if (!res.ok) {
      alert("Share failed. Please try again.");
      return;
    }
    alert("Shared.");
  };

  const handleEmail = async (note) => {
    const input = window.prompt("Email note to (comma-separated user IDs or emails):");
    if (!input) return;
    const tokens = input.split(",").map((s) => s.trim()).filter(Boolean);
    const emails = tokens.filter((t) => t.includes("@"));
    const userIds = tokens.filter((t) => !t.includes("@"));
    const res = await emailNoteToRecipients(note.id, { userIds, emails });
    if (!res.ok) {
      alert("Email failed. Please try again.");
      return;
    }
    alert("Email sent.");
  };

  const toggleExpand = (id) => {
    setExpandedNotes((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const sourceNotes = viewMode === "shared" ? sharedNotes : notes;
  const tags = Array.from(
    new Set(
      sourceNotes.flatMap((note) => (Array.isArray(note.tags) ? note.tags : []))
    )
  ).sort((a, b) => a.localeCompare(b));

  const filteredNotes = sourceNotes.filter((note) => {
    if (note.noteType === "JOURNAL") return false;
    if (selectedTag === "ALL") return true;
    const noteTags = Array.isArray(note.tags) ? note.tags : [];
    return noteTags.includes(selectedTag);
  });

  const sortedNotes = [...filteredNotes].sort((a, b) => {
    const pinA = a.pinned ? 1 : 0;
    const pinB = b.pinned ? 1 : 0;
    if (pinA !== pinB) return pinB - pinA;
    const favA = a.favorite ? 1 : 0;
    const favB = b.favorite ? 1 : 0;
    if (favA !== favB) return favB - favA;
    return (a.title || "").localeCompare(b.title || "");
  });


  return (
    <div className="note-list-wrap">
      <div className="note-list-header">
        <div>
          <h2>Notes</h2>
          <div className="note-list-subtitle">A focused space for your ideas, now with tags.</div>
        </div>
        <div className="note-list-actions">
          <div className="note-list-toggle">
            <button
              className={`note-list-toggle-btn ${viewMode === "mine" ? "active" : ""}`}
              onClick={() => setViewMode("mine")}
            >
              My notes
            </button>
            <button
              className={`note-list-toggle-btn ${viewMode === "shared" ? "active" : ""}`}
              onClick={() => setViewMode("shared")}
            >
              Shared with me
            </button>
          </div>
          <button className="note-list-journal-btn" onClick={() => nav("/journal")}>📓 Journal Today</button>
          {viewMode === "mine" && (
            <button className="note-list-new-btn" onClick={() => nav("new")}>➕ New</button>
          )}
        </div>
      </div>

      <div className="note-list-filters">
        <button
          className={`note-list-filter ${selectedTag === "ALL" ? "active" : ""}`}
          onClick={() => setSelectedTag("ALL")}
        >
          All
        </button>
        {tags.map((tag) => (
          <button
            key={tag}
            className={`note-list-filter ${selectedTag === tag ? "active" : ""}`}
            onClick={() => setSelectedTag(tag)}
          >
            {tag}
          </button>
        ))}
      </div>

      <div className="note-list-grid">
        {sortedNotes.map(n => (
          <div key={n.id} className={`note-list-card ${n.pinned ? "pinned" : ""}`}>
            <div className="note-list-card-top">
              <span className="note-list-title">{n.title}</span>
              <span className="note-list-badge">{n.noteType}</span>
            </div>

            <div className="note-list-flags">
              {n.pinned && <span className="note-list-flag">📌 Pinned</span>}
              {n.favorite && <span className="note-list-flag">⭐ Favorite</span>}
              {n.deleteProtected && <span className="note-list-flag">🛡 Protected</span>}
              {viewMode === "shared" && <span className="note-list-flag">🔗 Shared</span>}
              {n.reminderAt && (
                <span className="note-list-flag">
                  ⏰ {new Date(n.reminderAt).toLocaleString()}
                </span>
              )}
              {Array.isArray(n.attachments) && n.attachments.length > 0 && (
                <span className="note-list-flag">📎 {n.attachments.length} file(s)</span>
              )}
            </div>

            {Array.isArray(n.tags) && n.tags.length > 0 && (
              <div className="note-list-tags">
                {n.tags.map((tag) => (
                  <span key={tag} className="note-list-tag">
                    {tag}
                  </span>
                ))}
              </div>
            )}

            <div
              className={`note-list-body ${expandedNotes[n.id] ? "note-list-body-expanded" : ""}`}
              onClick={() => toggleExpand(n.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggleExpand(n.id);
                }
              }}
            >
              {extractPlainText(n.content)}
            </div>

            <button
              type="button"
              className="note-list-expand-btn"
              onClick={() => toggleExpand(n.id)}
            >
              {expandedNotes[n.id] ? "Show less" : "Show more"}
            </button>

            <div className="note-list-footer">
              <small>{n.createdDate}</small>
              <div>
                {viewMode === "mine" && (
                  <>
                    <button onClick={() => nav("new", { state: { note: n } })}>✏️</button>
                    <button onClick={() => handleShare(n)}>🔗 Share</button>
                    <button onClick={() => handleEmail(n)}>📧 Email</button>
                    <button onClick={() => remove(n)}>🗑</button>
                  </>
                )}
                {viewMode === "shared" && (
                  <button onClick={() => nav("new", { state: { note: n, readOnly: true } })}>
                    👁 View
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
